import { useEffect, useState } from "react";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { saveQueryData } from "../../../services/rules";
import { getAxiosResponseError } from "../../../utils/functions";
import { ruleQueryKey } from "../../../components/QueryBuilder/hooks/queryKeys";

/**
 * Save Report modal. Captures a name and POSTs to saveQueryData.
 * `disabled` is set by the parent when rule.hasSavedData is already set.
 */
export default function SaveReportModal({ ruleId, defaultName = "", disabled = false }) {
  const queryClient = useQueryClient();
  const [opened, { open, close }] = useDisclosure(false);
  const [name, setName] = useState("");

  useEffect(() => {
    if (opened) setName(defaultName);
    else setName("");
  }, [opened, defaultName]);

  const mutation = useMutation({
    mutationFn: saveQueryData,
    onSuccess: () => {
      toast.success("Report saved successfully!");
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
      close();
    },
    onError: (err) => toast.error(getAxiosResponseError(err)),
  });

  return (
    <>
      <Button variant="outline" disabled={disabled} onClick={open}>
        Save Report
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title="Save Report"
        centered
        size="md"
      >
        <Stack gap="md">
          <TextInput
            placeholder="Enter report name"
            value={name}
            onChange={(ev) => setName(ev.currentTarget.value)}
            autoFocus
          />
          <Group justify="space-between">
            <Button variant="outline" color="red" onClick={close}>
              Cancel
            </Button>
            <Button
              color="green"
              variant="outline"
              disabled={!name}
              loading={mutation.isPending}
              onClick={() => mutation.mutate({ ruleId, name })}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
